import type { LensCard, LensDatasetColumn, LensTableColumn, LensValueFormat } from '~/types/leLens';

import type { Shaped } from '../cardView';
import { UNIT_FRACTION_PERCENT, displayLensFieldName, fieldFormat, isDateLikeText, isNumeric } from '../lensChart';
import { namedTableRatios } from '../namedTableRatios';

export type ComparisonTableColumn = LensTableColumn;
type DisplayColumn = ComparisonTableColumn & { name: string };
export type ComparisonTable = {
  shaped: Shaped;
  columns: Array<DisplayColumn>;
  notice?: string;
};
const KINDS = new Set(['group', 'share', 'current', 'prior', 'change', 'change_pct']);
const COMPARISON_KINDS = new Set(['prior', 'change', 'change_pct']);

function numeric(value: unknown): number | null {
  return isNumeric(value) && Number.isFinite(Number(value)) ? Number(value) : null;
}

function savedColumns(value: unknown): Array<ComparisonTableColumn> | undefined {
  if (!Array.isArray(value) || !value.length) return undefined;
  if (
    value.some(
      (column) => !column || typeof column !== 'object' || !KINDS.has(column.kind) || typeof column.field !== 'string',
    )
  )
    return undefined;
  return value;
}

/** Formats are per named measure: a percent ratio must not reformat its currency/count siblings. */
export function namedTableRatioFormats(
  card: LensCard,
  datasetColumns: ReadonlyArray<LensDatasetColumn> = [],
): Record<string, LensValueFormat> {
  return Object.fromEntries(
    namedTableRatios(card.view, card.type, datasetColumns).map((column) => [
      column.name,
      card.config?.fieldFormats?.[column.name] ?? column.format ?? UNIT_FRACTION_PERCENT,
    ]),
  );
}

/** Join complete companion groups without turning absent or null values into zero. */
export function buildComparisonTable({
  card,
  current,
  comparison,
  hasComparison,
  currentComplete,
  ratioFormat,
  ratioFormats = {},
}: {
  card: LensCard;
  current: Shaped;
  comparison?: Shaped;
  hasComparison: boolean;
  currentComplete: boolean;
  ratioFormat?: LensValueFormat;
  ratioFormats?: Readonly<Record<string, LensValueFormat>>;
}): ComparisonTable {
  const allRows = [...current.rows, ...(comparison?.rows ?? [])];
  const percentagePoints = (field: string) => {
    const format = ratioFormats[field] ?? (card.view?.ratio ? ratioFormat : undefined);
    return format?.kind === 'percent' && format.percentScale === 'unit';
  };
  const fields = [...new Set([...current.columns, ...(comparison?.columns ?? [])])];
  const axes = [card.view?.x, card.view?.series].filter((field): field is string =>
    Boolean(field && fields.includes(field)),
  );
  const saved = savedColumns(card.config?.tableColumns);
  const declaredMeasures = [
    card.view?.value,
    ...(card.view?.x || card.view?.series ? (card.view?.values ?? []) : []),
    ...(card.view?.ratio ? [card.view.ratio.of] : []),
    ...(saved?.filter((column) => column.kind !== 'group').map((column) => column.field) ?? []),
  ];
  const groups = fields.filter(
    (field) =>
      axes.includes(field) || (!declaredMeasures.includes(field) && !allRows.some((row) => isNumeric(row[field]))),
  );
  const measures = fields.filter((field) => !groups.includes(field));
  const requested = saved ?? [
    ...groups.map((field): ComparisonTableColumn => ({ kind: 'group', field })),
    ...measures.flatMap(
      (field): Array<ComparisonTableColumn> => [
        { kind: 'current', field },
        ...(hasComparison
          ? ([
              { kind: 'prior', field },
              { kind: 'change', field },
              { kind: 'change_pct', field },
            ] as Array<ComparisonTableColumn>)
          : []),
      ],
    ),
  ];
  const visible = requested.filter(
    (column) =>
      fields.includes(column.field) &&
      (hasComparison || !COMPARISON_KINDS.has(column.kind)) &&
      (column.kind !== 'share' || (!card.view?.ratio && !ratioFormats[column.field])),
  );
  const used = new Set(fields);
  const outputNames = new Set<string>();
  const columns = visible.map((column): DisplayColumn => {
    if ((column.kind === 'group' || column.kind === 'current') && !outputNames.has(column.field)) {
      outputNames.add(column.field);
      return { ...column, name: column.field };
    }
    const stem = `${column.field}__${column.kind}`;
    let name = stem;
    let suffix = 2;
    while (used.has(name)) name = `${stem}_${suffix++}`;
    used.add(name);
    outputNames.add(name);
    return { ...column, name };
  });
  const key = (row: Record<string, unknown>) => JSON.stringify(groups.map((field) => row[field]));
  const unique = (rows: Shaped['rows']) => new Set(rows.map(key)).size === rows.length;
  const comparable = Boolean(comparison && unique(current.rows) && unique(comparison.rows));
  const priorByKey = new Map(comparable ? comparison!.rows.map((row) => [key(row), row]) : []);
  const currentByKey = new Map(current.rows.map((row) => [key(row), row]));
  const pairs: Array<{ current?: Record<string, unknown>; prior?: Record<string, unknown> }> = current.rows.map(
    (row) => ({
      current: row,
      prior: priorByKey.get(key(row)),
    }),
  );
  if (comparable) {
    comparison!.rows.forEach((row) => {
      if (!currentByKey.has(key(row))) pairs.push({ prior: row });
    });
  }
  const totals = new Map<string, number | null>();
  for (const column of columns.filter((entry) => entry.kind === 'share')) {
    const values = current.rows.map((row) => numeric(row[column.field]));
    totals.set(
      column.field,
      currentComplete && values.every((value) => value !== null)
        ? values.reduce<number>((sum, value) => sum + value!, 0)
        : null,
    );
  }
  const rows = pairs.map((pair) =>
    Object.fromEntries(
      columns.map((column) => {
        const value = pair.current?.[column.field] ?? null;
        const prior = pair.prior?.[column.field] ?? null;
        const currentNumber = numeric(value);
        const priorNumber = numeric(prior);
        const delta =
          currentComplete && currentNumber !== null && priorNumber !== null ? currentNumber - priorNumber : null;
        let output: unknown;
        switch (column.kind) {
          case 'group':
            output = pair.current?.[column.field] ?? pair.prior?.[column.field] ?? null;
            break;
          case 'current':
            output = value;
            break;
          case 'prior':
            output = prior;
            break;
          case 'change':
            output = delta === null ? null : percentagePoints(column.field) ? delta * 100 : delta;
            break;
          case 'change_pct':
            output = delta === null || priorNumber === 0 ? null : delta / Math.abs(priorNumber!);
            break;
          case 'share': {
            const total = totals.get(column.field);
            output = currentNumber === null || total == null || total === 0 ? null : currentNumber / total;
            break;
          }
        }
        return [column.name, output];
      }),
    ),
  );
  return {
    shaped: { columns: columns.map((column) => column.name), rows },
    columns,
    notice:
      hasComparison && !comparable
        ? 'Comparison rows are unavailable or cannot be matched uniquely.'
        : hasComparison &&
            groups.some((field) =>
              allRows.some(
                (row) => row[field] instanceof Date || (typeof row[field] === 'string' && isDateLikeText(row[field])),
              ),
            )
          ? 'Dates match only when identical. Other dates remain separate.'
          : undefined,
  };
}

export function comparisonTableConfig(
  config: LensCard['config'],
  columns: ComparisonTable['columns'],
  comparisonLabel: string,
  ratioFormat?: LensValueFormat,
  ratioFormats: Readonly<Record<string, LensValueFormat>> = {},
): NonNullable<LensCard['config']> {
  const focus = columns.find((column) => column.kind === 'current')?.field;
  const labels: Record<string, string> = {};
  const formats: Record<string, LensValueFormat> = {};
  for (const column of columns) {
    const baseLabel = displayLensFieldName(column.field, config);
    const measureRatioFormat = ratioFormats[column.field] ?? ratioFormat;
    const baseFormat = measureRatioFormat ?? fieldFormat(config, column.field);
    const percentagePoints = measureRatioFormat?.kind === 'percent' && measureRatioFormat.percentScale === 'unit';
    const focused = column.field === focus;
    switch (column.kind) {
      case 'group':
        if (column.name !== column.field) labels[column.name] = baseLabel;
        break;
      case 'current':
        if (column.name !== column.field) labels[column.name] = baseLabel;
        if (baseFormat) formats[column.name] = baseFormat;
        break;
      case 'prior':
        labels[column.name] = focused ? comparisonLabel : `${baseLabel} (${comparisonLabel})`;
        if (baseFormat) formats[column.name] = baseFormat;
        break;
      case 'change':
        labels[column.name] = `${focused ? 'Change' : `Change in ${baseLabel}`}${percentagePoints ? ' (pp)' : ''}`;
        if (percentagePoints) formats[column.name] = { kind: 'number', decimals: baseFormat?.decimals ?? 1 };
        else if (baseFormat) formats[column.name] = baseFormat;
        break;
      case 'change_pct':
        labels[column.name] = focused ? 'Change %' : `${baseLabel} change %`;
        formats[column.name] = { ...UNIT_FRACTION_PERCENT, decimals: 1 };
        break;
      case 'share':
        labels[column.name] = 'Share';
        formats[column.name] = { ...UNIT_FRACTION_PERCENT, decimals: 1 };
        break;
    }
  }
  return {
    ...config,
    fieldLabels: { ...config?.fieldLabels, ...labels },
    fieldFormats: { ...config?.fieldFormats, ...formats },
  };
}
